import { useCommunityConfig } from '../../CommunityConfig'
import type { Channel } from '../../CommunityConfig'

type MentionsNeedsRefresh<ChannelShape extends Channel> = {
  /** The mention-capable channels in scope that are blocked. */
  channelsNeedingRefresh: ChannelShape[]
  /**
   * Whether every mention-capable channel in scope is blocked — the condition
   * under which the mentions feed shows the refresh empty state and the views
   * hide the mentions filters.
   */
  allChannelsNeedRefresh: boolean
}

/**
 * One derivation for "the mentions inbox is dead until a reconnect", shared by
 * the feed (which swaps in the refresh empty state) and the views (which hide
 * the filters — there is nothing to filter). Channels that cannot produce
 * mentions don't count either way.
 */
export function useMentionsNeedsRefresh<ChannelShape extends Channel>(
  channels: ChannelShape[],
): MentionsNeedsRefresh<ChannelShape> {
  const {
    config: { supportsMentions, channelNeedsRefreshForMentions },
  } = useCommunityConfig()

  const mentionChannels = channels.filter(supportsMentions)
  const channelsNeedingRefresh = mentionChannels.filter(
    channelNeedsRefreshForMentions,
  )
  return {
    channelsNeedingRefresh,
    allChannelsNeedRefresh:
      mentionChannels.length > 0 &&
      channelsNeedingRefresh.length === mentionChannels.length,
  }
}
